import { LabelWithTimezone } from './LabelWithTimezone';
import DayPicker from '@rio-cloud/rio-uikit/DayPicker';
import TimePicker from '@rio-cloud/rio-uikit/TimePicker';
import { applyTimeToDate, getTimeAsString, isValidDate } from './dateHelper';
import { formatLocalizedDateTimeInTimeZone, parseLocalizedDateTime } from './localizationHelper';
import { useIntl } from 'react-intl';
import { useId, useMemo, useRef, useState } from 'react';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { ValidationResult } from './validationResult.type';

export type DayAndTimePickerProps = {
    readonly labelId: string;
    readonly timeZone: string;
    readonly date: Date | undefined;
    readonly validation: ValidationResult;
    readonly onDateChange: (value: Date | undefined) => void;
    readonly isDisabled: boolean;
    readonly onParseError: (hasParseError: boolean) => void;
};

// eslint-disable-next-line max-lines-per-function
export const DayAndTimePicker = ({
    labelId,
    timeZone,
    date,
    validation,
    onDateChange,
    isDisabled,
    onParseError,
}: DayAndTimePickerProps) => {
    const intl = useIntl();
    const inputId = useId();
    const [hasParseError, setHasParseError] = useState(false);

    // Needed since handleValidSelectedDateChange needs the lastest time changes of
    // handleManualDateInputChange happening in the same render when the user manually enters a time
    const latestValidTime = useRef<string | undefined>(undefined);
    const validDate = isValidDate(date) ? date : undefined;
    const displayDate = useMemo(() => (date ? toZonedTime(date, timeZone) : undefined), [date, timeZone]);

    const toUtcDate = (nextValue: Date | undefined) =>
        nextValue === undefined ? undefined : fromZonedTime(nextValue, timeZone);

    const handleTimeChange = (timeValue: string) => {
        onDateChange(toUtcDate(applyTimeToDate(displayDate, timeValue)));
        latestValidTime.current = timeValue;
    };

    const onInputParsing = (hasValidInput: boolean) => {
        const hasInvalidInput = !hasValidInput;
        if (hasParseError !== hasInvalidInput) {
            setHasParseError(hasInvalidInput);
            onParseError(hasInvalidInput);
        }
    };

    // This is called when the DayPicker commits a date value change. That can happen after selecting a day from the
    // calendar, clearing the picker, or after the text input produced a valid parsed date that the DayPicker accepts
    // as its selected value.
    // Time changes via manual typing are reflected in the nextDate,
    // but when selecting a date in the calendar the nextDate is 00:00
    const handleValidSelectedDateChange = (nextDate: Date | undefined) => {
        const time = latestValidTime.current ?? getTimeAsString(nextDate);
        onDateChange(toUtcDate(applyTimeToDate(nextDate, time)));
        onInputParsing(true);
    };

    // This is called when the user changes the text input value of the picker. So it runs while the user types,
    // clears, pastes, or otherwise edits the date text manually.
    const handleManualDateInputChange = (rawString: string, parsedDate: Date | undefined, isValidInput: boolean) => {
        onInputParsing(isValidInput || rawString === '');
        if (isValidInput) {
            latestValidTime.current = getTimeAsString(parsedDate);
        }
    };

    const parseDateTimeValue = (rawValue: string) => parseLocalizedDateTime(rawValue, intl.locale);
    const renderDate = () => (date ? formatLocalizedDateTimeInTimeZone(date, timeZone, intl.locale) : '');

    return (
        <>
            <LabelWithTimezone htmlFor={inputId} messageId={labelId} timeZoneData={{ timeZone, date: validDate }} />
            <DayPicker
                id={inputId}
                mode="single"
                isClearable
                locale={intl.locale}
                placeholder={intl.formatMessage({
                    id: 'outboundPortal.common.chooseDatePlaceholder',
                })}
                inputProps={{ disabled: isDisabled }}
                closeOnSelect={false}
                value={displayDate}
                renderValue={renderDate}
                onChange={handleValidSelectedDateChange}
                onInputChange={handleManualDateInputChange}
                parseInput={parseDateTimeValue}
                className="margin-bottom-0"
                errorMessage={
                    (hasParseError && intl.formatMessage({ id: 'outboundPortal.common.invalidDate' })) ||
                    (validation.error && intl.formatMessage({ id: validation.error }))
                }
                warningMessage={validation.warning && intl.formatMessage({ id: validation.warning })}
                footer={() => (
                    <TimePicker
                        value={getTimeAsString(displayDate)}
                        onChange={handleTimeChange}
                        dropdown
                        showIcon
                        disabled={isDisabled}
                    />
                )}
            />
        </>
    );
};
